// Certificado digital do embarcador (PO1-3008): o cliente envia o A1 (.pfx) da própria
// empresa para a Everlog capturar os CT-e em que ele é tomador direto na SEFAZ
// (CTeDistribuicaoDFe, épico PO1-3002). O arquivo e a senha vão para o cofre na AWS;
// aqui só aparecem os metadados que o backend devolve (CNPJ, validade, série).
//
// Padrão da página (PO1-2724/2728): PageHero + ShellCard por token, `useCompanies` com
// bloqueio quando não há embarcador específico (MinhasTransportadoras), remoção em dois
// cliques (EditCotacaoModal) — sem window.confirm nem SweetAlert.
//
// Regras que a tela garante do lado dela:
//   - a senha vive num state, vai UMA vez no POST e é zerada depois (sucesso ou erro);
//   - o envio só habilita com arquivo válido + senha + termo aceito na versão vigente;
//   - um certificado por RAIZ de CNPJ: subir outro da mesma raiz substitui o anterior, e
//     a tela avisa isso antes (aviso) e depois (toast).
//
// PO1-3428: toda chamada leva o `idEmbarcador` da tela (useCompanies) em query, para o
// backend não depender do header do embarcador ativo quando há mais de uma aba aberta.
// PO1-3426: o card de auditoria mostra o log paginado no servidor e o detalhe por consulta.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Col, Collapse, FormGroup, Input, Label, Row, Spinner, Table } from "reactstrap";

import PageHero, { PageHeroRefreshButton } from "../../../components/PageHero";
import ShellCard, { TableSurface } from "../../../components/ShellCard";
import SectionCard from "../../../components/SectionCard";
import SpecificEmpresaRequiredNotice from "../../../components/SpecificEmpresaRequiredNotice";
import TablePagination from "../../../components/TablePagination";
import { AUTOPILOT_COLORS } from "../../../components/autopilot/styles";
import { useCompanies } from "../../../hooks/companies";
import { useToast } from "../../../hooks/toast";
import { formatDate, formatDateTime } from "../../../utils/formatDate";
import {
  CERTIFICADO_ACCEPT,
  CERTIFICADO_LEGENDA,
  validarArquivoCertificado,
  obterTermo,
  listarCertificados,
  enviarCertificado,
  removerCertificado,
  mensagemErroCertificado,
  formatarCnpj,
  formatarRaiz,
  situacaoCertificado,
  exportarLogCaptura,
  listarLogCaptura,
  obterLogCaptura,
  mensagemErroBlob,
} from "../../../services/embarcadorCertificadoApi";
import { EstadoTabela, ModalHub, fmtDataHora } from "../_documentosFrete/comum";
import "../_documentosFrete/styles.css";

// Paginação do log no servidor (PO1-3426), mesmo padrão de NfseSemNotas.
const PAGE_SIZES = [20, 50, 100];

const linhaStyle = {
  display: "flex",
  alignItems: "flex-start",
  gap: "0.9rem",
  padding: "0.85rem 0",
  borderBottom: `1px solid ${AUTOPILOT_COLORS.divider}`,
  flexWrap: "wrap",
};
const tituloLinhaStyle = { color: AUTOPILOT_COLORS.title, fontSize: "0.95rem", fontWeight: 600 };
const metaStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem", marginTop: 3, lineHeight: 1.5 };
const monoStyle = { fontVariantNumeric: "tabular-nums", letterSpacing: "0.01em" };
const vazioStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.86rem", padding: "0.5rem 0" };
const legendaStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.74rem", display: "block", marginTop: 4 };
const erroStyle = {
  background: AUTOPILOT_COLORS.statusDangerBg,
  border: `1px solid ${AUTOPILOT_COLORS.statusDangerBorder}`,
  color: AUTOPILOT_COLORS.text,
  borderRadius: 8,
  padding: "0.6rem 0.8rem",
  fontSize: "0.84rem",
  marginTop: "0.75rem",
};
const avisoStyle = {
  background: AUTOPILOT_COLORS.statusWarningBg,
  border: `1px solid ${AUTOPILOT_COLORS.statusWarningBorder}`,
  color: AUTOPILOT_COLORS.text,
  borderRadius: 8,
  padding: "0.6rem 0.8rem",
  fontSize: "0.84rem",
  marginTop: "0.75rem",
};
const termoBoxStyle = {
  maxHeight: 260,
  overflowY: "auto",
  fontSize: "0.84rem",
  lineHeight: 1.55,
  color: AUTOPILOT_COLORS.text,
  padding: "0.25rem 0.25rem 0.25rem 0",
};
const termoItemTituloStyle = { fontWeight: 600, color: AUTOPILOT_COLORS.title };

function CnpjsCobertos({ lista }) {
  const cnpjs = Array.isArray(lista) ? lista : [];
  if (!cnpjs.length) return <span>Nenhum CNPJ do seu cadastro tem esta raiz.</span>;
  return (
    <span>
      Cobre {cnpjs.length} CNPJ{cnpjs.length === 1 ? "" : "s"} do seu cadastro:{" "}
      <span style={monoStyle}>{cnpjs.map(formatarCnpj).join(", ")}</span>
    </span>
  );
}

function LinhaCertificado({ cert, confirmando, removendo, ocupado, onPedirRemocao, onConfirmar, onCancelar }) {
  const situacao = situacaoCertificado(cert);
  const vencidoOuVencendo = situacao.chave === "vencido" || situacao.chave === "vencendo";
  return (
    <div style={linhaStyle} data-testid="certificado-linha">
      <i
        className="tim-icons icon-lock-circle"
        style={{ color: AUTOPILOT_COLORS.accent, fontSize: "1.15rem", marginTop: 3, flexShrink: 0 }}
      />
      <div style={{ flex: 1, minWidth: 240 }}>
        <div style={tituloLinhaStyle}>
          {cert.razaoSocial || "Certificado A1"}{" "}
          <Badge color={situacao.cor} pill style={{ marginLeft: 6, verticalAlign: "middle" }}>
            {situacao.rotulo}
          </Badge>
        </div>
        <div style={metaStyle}>
          CNPJ do certificado <span style={monoStyle}>{formatarCnpj(cert.cnpjCertificado)}</span> · raiz{" "}
          <span style={monoStyle}>{formatarRaiz(cert.cnpjRaiz)}</span>
          <br />
          Válido de <span style={monoStyle}>{formatDate(cert.validadeInicio)}</span> até{" "}
          <strong style={{ ...monoStyle, color: vencidoOuVencendo ? AUTOPILOT_COLORS.statusDanger : "inherit" }}>
            {formatDate(cert.validadeFim)}
          </strong>
          {cert.numeroSerie ? (
            <>
              {" "}· série <span style={monoStyle}>{cert.numeroSerie}</span>
            </>
          ) : null}
          <br />
          <CnpjsCobertos lista={cert.cnpjsCobertos} />
          <br />
          Enviado em {formatDateTime(cert.dataUpload)}
          {cert.termoVersao ? ` · termo ${cert.termoVersao} aceito em ${formatDateTime(cert.termoAceiteEm)}` : ""}
        </div>
        {situacao.chave === "vencido" ? (
          <div style={erroStyle}>
            Certificado vencido: a captura na SEFAZ está parada para esta raiz de CNPJ. Envie o certificado renovado.
          </div>
        ) : null}
        {cert.ultimaRejeicaoCStat ? (
          <div style={erroStyle}>
            A SEFAZ rejeitou este certificado em {formatDateTime(cert.ultimaRejeicaoEm)} (cStat {cert.ultimaRejeicaoCStat}:{" "}
            {cert.ultimaRejeicaoMotivo || "motivo não informado"}). A captura está parada para esta raiz até um certificado válido
            ser enviado.
          </div>
        ) : null}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", flexShrink: 0 }}>
        {confirmando ? (
          <>
            <span style={{ color: AUTOPILOT_COLORS.statusDanger, fontSize: "0.78rem", marginRight: 4 }}>
              A captura na SEFAZ para esta raiz para na hora. Remover?
            </span>
            <Button color="danger" size="sm" disabled={removendo} onClick={onConfirmar}>
              {removendo ? "Removendo..." : "Confirmar remoção"}
            </Button>
            <Button color="secondary" size="sm" outline disabled={removendo} onClick={onCancelar}>
              Cancelar
            </Button>
          </>
        ) : (
          <Button
            color="danger"
            size="sm"
            outline
            disabled={ocupado}
            onClick={onPedirRemocao}
            title="Remover o certificado do cofre"
          >
            <i className="tim-icons icon-trash-simple" style={{ marginRight: 4 }} /> Remover
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Log da captura (PO1-3426) ───────────────────────────────────────────────
const num = (v) => (v === null || v === undefined || v === "" ? 0 : Number(v) || 0);
const faixaNsu = (item) => {
  const ini = item.nsuInicial;
  const fim = item.nsuFinal;
  if ((ini === null || ini === undefined) && (fim === null || fim === undefined)) return "-";
  if (ini === fim || fim === null || fim === undefined) return String(ini);
  return `${ini} – ${fim}`;
};
// Eventos = os que não foram gravados mais os gravados (campo novo, pode não existir).
const totalEventos = (item) => num(item.eventosIgnorados) + num(item.eventosGravados);

function LinhaLog({ item, onAbrir }) {
  // Vermelho no código da SEFAZ só quando a CONSULTA falhou (rede, TLS, cStat de rejeição).
  // Documento com erro dentro de um 138 é destacado na coluna "Erros", não no 138.
  const consultaFalhou = (item.status && item.status !== "ok") || Boolean(item.erro);
  const temErros = num(item.erros) > 0;
  return (
    <tr
      className="is-clicavel"
      role="button"
      tabIndex={0}
      onClick={onAbrir}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onAbrir();
        }
      }}
      title="Ver os documentos desta consulta"
      data-testid="certificado-log-linha"
    >
      <td className="docs-frete__nowrap">{fmtDataHora(item.criadoEm)}</td>
      <td className="docs-frete__nowrap" style={monoStyle}>
        {formatarCnpj(item.cnpj) || "-"}
      </td>
      <td className="docs-frete__nowrap" style={monoStyle}>
        {faixaNsu(item)}
      </td>
      <td>
        <strong style={consultaFalhou ? { color: AUTOPILOT_COLORS.statusDanger } : undefined} data-testid="certificado-log-cstat" data-destaque={consultaFalhou ? "erro" : undefined}>{item.cStat || (item.status && item.status !== "ok" ? item.status : "-")}</strong>
        <small>{item.xMotivo || item.erro || ""}</small>
      </td>
      <td className="is-number">{num(item.quantidadeDocumentos)}</td>
      <td className="is-number">{num(item.gravados)}</td>
      <td className="is-number">{num(item.ignorados)}</td>
      <td className="is-number">
        {/* cor no elemento interno: a regra de cor do tema para td sobrescreve o style da célula */}
        <span style={temErros ? { color: AUTOPILOT_COLORS.statusDanger, fontWeight: 700 } : undefined} data-testid="certificado-log-erros" data-destaque={temErros ? "erro" : undefined}>
          {num(item.erros)}
        </span>
      </td>
      <td className="is-number">
        {totalEventos(item)}
        {item.eventosGravados !== undefined && item.eventosGravados !== null ? <small>{num(item.eventosGravados)} gravados</small> : null}
      </td>
    </tr>
  );
}

function DetalheLogModal({ item, detalhe, carregando, erro, onClose }) {
  const documentos = detalhe && Array.isArray(detalhe.documentos) ? detalhe.documentos : [];
  const temEvento = documentos.some((d) => d.tpEvento !== undefined && d.tpEvento !== null && d.tpEvento !== "");
  return (
    <ModalHub titulo={`Consulta à SEFAZ · ${fmtDataHora(item.criadoEm)}`} icone="tim-icons icon-zoom-split" onClose={onClose} maxWidth={1000}>
      <div style={{ ...metaStyle, marginTop: 0, marginBottom: "0.75rem" }} data-testid="certificado-log-resumo">
        CNPJ <span style={monoStyle}>{formatarCnpj(item.cnpj) || "-"}</span> · NSU <span style={monoStyle}>{faixaNsu(item)}</span>
        {item.maxNSU !== undefined && item.maxNSU !== null ? (
          <>
            {" "}
            · máx. <span style={monoStyle}>{item.maxNSU}</span>
          </>
        ) : null}
        <br />
        cStat <span style={monoStyle}>{item.cStat || "-"}</span>
        {item.xMotivo ? ` · ${item.xMotivo}` : ""}
        {item.erro ? ` · ${item.erro}` : ""}
        <br />
        {num(item.quantidadeDocumentos)} documento(s) · {num(item.gravados)} gravado(s) · {num(item.ignorados)} já existente(s) · {num(item.erros)} erro(s) ·{" "}
        {totalEventos(item)} evento(s)
        {item.duracaoMs !== undefined && item.duracaoMs !== null ? ` · ${num(item.duracaoMs)} ms` : ""}
      </div>
      <TableSurface>
        <EstadoTabela
          loading={carregando}
          erro={erro}
          vazio={!carregando && !erro && detalhe && !documentos.length}
          textoCarregando="Carregando os documentos..."
          textoVazio="Esta consulta não trouxe documentos."
        />
        {!carregando && !erro && documentos.length ? (
          <Table className="docs-frete__tabela hub-thead-accent" size="sm" responsive data-testid="certificado-log-documentos">
            <thead className="text-primary">
              <tr>
                <th>Chave de acesso</th>
                <th>Código</th>
                <th>Mensagem</th>
                {temEvento ? <th>Evento</th> : null}
                <th className="is-number">NSU</th>
                <th>Schema</th>
              </tr>
            </thead>
            <tbody>
              {documentos.map((d, i) => (
                <tr key={`${d.nsu || i}-${d.chaveAcesso || i}`}>
                  <td className="docs-frete__chave">{d.chaveAcesso || "-"}</td>
                  <td className="docs-frete__nowrap" style={monoStyle}>
                    {d.code !== undefined && d.code !== null && d.code !== "" ? d.code : "-"}
                  </td>
                  <td>{d.message || "-"}</td>
                  {temEvento ? <td className="docs-frete__nowrap">{d.tpEvento || "-"}</td> : null}
                  <td className="is-number" style={monoStyle}>
                    {d.nsu !== undefined && d.nsu !== null ? d.nsu : "-"}
                  </td>
                  <td className="docs-frete__nowrap">{d.schema || "-"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : null}
      </TableSurface>
      <div className="docs-frete__modal-rodape">
        <Button color="secondary" size="sm" onClick={onClose}>
          Fechar
        </Button>
      </div>
    </ModalHub>
  );
}

export default function CertificadoDigital() {
  const { empresa, hasEmpresaSelection } = useCompanies();
  const { addToast } = useToast();
  const embarcadorId = empresa?.value || null;

  const [certificados, setCertificados] = useState([]);
  const [termo, setTermo] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erroLista, setErroLista] = useState("");

  const [arquivo, setArquivo] = useState(null);
  const [erroArquivo, setErroArquivo] = useState("");
  const [senha, setSenha] = useState("");
  const [aceite, setAceite] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erroEnvio, setErroEnvio] = useState("");

  const [confirmandoId, setConfirmandoId] = useState(null);
  const [removendoId, setRemovendoId] = useState(null);
  const [historicoAberto, setHistoricoAberto] = useState(false);

  // Auditoria (PO1-3010): período do CSV, padrão = últimos 30 dias.
  const hojeIso = new Date().toISOString().slice(0, 10);
  const [logDe, setLogDe] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10));
  const [logAte, setLogAte] = useState(hojeIso);
  const [exportando, setExportando] = useState(false);
  const [erroExportacao, setErroExportacao] = useState("");

  // Log paginado (PO1-3426): os filtros de/até só valem para a tabela quando o usuário
  // consulta; o CSV continua lendo os campos direto.
  const [logFiltros, setLogFiltros] = useState(() => ({ de: logDe, ate: logAte }));
  const [logItens, setLogItens] = useState([]);
  const [logTotal, setLogTotal] = useState(0);
  const [logPage, setLogPage] = useState(1);
  const [logPageSize, setLogPageSize] = useState(PAGE_SIZES[0]);
  const [logCarregando, setLogCarregando] = useState(false);
  const [erroLog, setErroLog] = useState("");
  const [logAberto, setLogAberto] = useState(null);
  const [logDetalhe, setLogDetalhe] = useState(null);
  const [logDetalheCarregando, setLogDetalheCarregando] = useState(false);
  const [erroLogDetalhe, setErroLogDetalhe] = useState("");

  const fileInputRef = useRef(null);
  const montadoRef = useRef(true);

  useEffect(() => {
    montadoRef.current = true;
    return () => {
      montadoRef.current = false;
    };
  }, []);

  const carregar = useCallback(async () => {
    if (!embarcadorId) return;
    setCarregando(true);
    setErroLista("");
    try {
      // O termo vem junto em toda carga: é pequeno e assim a versão exibida é sempre a vigente.
      const [lista, termoAtual] = await Promise.all([listarCertificados(embarcadorId), obterTermo(embarcadorId)]);
      if (!montadoRef.current) return;
      setCertificados(lista);
      if (termoAtual) setTermo(termoAtual);
    } catch (err) {
      if (montadoRef.current) setErroLista(mensagemErroCertificado(err, "Não foi possível carregar os certificados."));
    } finally {
      if (montadoRef.current) setCarregando(false);
    }
  }, [embarcadorId]);

  // Trocou de embarcador: recarrega e zera qualquer estado de formulário/confirmação.
  useEffect(() => {
    setConfirmandoId(null);
    setErroEnvio("");
    setErroArquivo("");
    setArquivo(null);
    setSenha("");
    setAceite(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setLogAberto(null);
    setLogPage(1);
    carregar();
  }, [embarcadorId, carregar]);

  // Log da captura (PO1-3426): consulta paginada no servidor. Recarrega quando muda o
  // embarcador, o período aplicado, a página ou o tamanho.
  const carregarLog = useCallback(async () => {
    if (!embarcadorId) return;
    setLogCarregando(true);
    setErroLog("");
    try {
      const resultado = await listarLogCaptura({
        idEmbarcador: embarcadorId,
        de: logFiltros.de,
        ate: logFiltros.ate,
        page: logPage,
        pageSize: logPageSize,
      });
      if (!montadoRef.current) return;
      setLogItens(resultado.itens);
      setLogTotal(resultado.total);
    } catch (err) {
      if (montadoRef.current) {
        setLogItens([]);
        setLogTotal(0);
        setErroLog(mensagemErroCertificado(err, "Não foi possível consultar o log da captura."));
      }
    } finally {
      if (montadoRef.current) setLogCarregando(false);
    }
  }, [embarcadorId, logFiltros, logPage, logPageSize]);

  useEffect(() => {
    carregarLog();
  }, [carregarLog]);

  // Detalhe da consulta aberta: busca a linha inteira (com `documentos`) ao abrir o modal.
  useEffect(() => {
    if (!logAberto || !embarcadorId) {
      setLogDetalhe(null);
      setErroLogDetalhe("");
      return undefined;
    }
    let ativo = true;
    setLogDetalhe(null);
    setErroLogDetalhe("");
    setLogDetalheCarregando(true);
    obterLogCaptura({ idEmbarcador: embarcadorId, logId: logAberto._id })
      .then((doc) => ativo && setLogDetalhe(doc || {}))
      .catch((err) => ativo && setErroLogDetalhe(mensagemErroCertificado(err, "Não foi possível carregar os documentos desta consulta.")))
      .finally(() => ativo && setLogDetalheCarregando(false));
    return () => {
      ativo = false;
    };
  }, [logAberto, embarcadorId]);

  const ativos = useMemo(() => certificados.filter((c) => c.ativo), [certificados]);
  const historico = useMemo(
    () => certificados.filter((c) => !c.ativo).sort((a, b) => String(b.dataRemocao || "").localeCompare(String(a.dataRemocao || ""))),
    [certificados]
  );

  const raizesAtivas = useMemo(() => ativos.map((c) => String(c.cnpjRaiz || "")), [ativos]);

  const handleSelecionar = (event) => {
    const file = event.target.files && event.target.files[0];
    setErroEnvio("");
    if (!file) {
      setArquivo(null);
      setErroArquivo("");
      return;
    }
    const { valido, erro } = validarArquivoCertificado(file);
    setArquivo(valido ? file : null);
    setErroArquivo(valido ? "" : erro);
    if (!valido) event.target.value = "";
  };

  const limparFormulario = () => {
    setArquivo(null);
    setSenha("");
    setAceite(false);
    setErroArquivo("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const podeEnviar = Boolean(arquivo && senha && aceite && termo && termo.versao && !enviando && !carregando);

  const handleEnviar = async (event) => {
    if (event && event.preventDefault) event.preventDefault();
    if (!podeEnviar) return;
    setEnviando(true);
    setErroEnvio("");
    const senhaEnvio = senha;
    // A senha sai do state antes mesmo da resposta: se a aba ficar aberta, não sobra nada.
    setSenha("");
    try {
      const criado = await enviarCertificado({ file: arquivo, senha: senhaEnvio, termoVersao: termo.versao, idEmbarcador: embarcadorId });
      const substituiu = Number(criado && criado.substituidos) > 0;
      addToast({
        place: "tr",
        type: "success",
        title: substituiu ? "Certificado substituído" : "Certificado enviado",
        description: substituiu
          ? `O certificado anterior da raiz ${formatarRaiz(criado.cnpjRaiz)} foi substituído. A captura segue com o novo.`
          : `Certificado ${formatarCnpj(criado && criado.cnpjCertificado)} guardado no cofre. A captura na SEFAZ passa a usá-lo no próximo ciclo.`,
      });
      limparFormulario();
      await carregar();
    } catch (err) {
      if (montadoRef.current) setErroEnvio(mensagemErroCertificado(err, "Não foi possível enviar o certificado."));
    } finally {
      if (montadoRef.current) setEnviando(false);
    }
  };

  const handleRemover = async (cert) => {
    setRemovendoId(cert.id);
    setErroLista("");
    try {
      await removerCertificado(cert.id, embarcadorId);
      setConfirmandoId(null);
      addToast({
        place: "tr",
        type: "success",
        title: "Certificado removido",
        description: `O certificado ${formatarCnpj(cert.cnpjCertificado)} saiu do cofre. A captura na SEFAZ para esta raiz foi interrompida.`,
      });
      await carregar();
    } catch (err) {
      if (montadoRef.current) {
        addToast({ place: "tr", type: "danger", title: "Erro", description: mensagemErroCertificado(err, "Não foi possível remover o certificado.") });
      }
    } finally {
      if (montadoRef.current) setRemovendoId(null);
    }
  };

  const periodoInvalido = Boolean(logDe && logAte && logDe > logAte);

  const handleConsultarLog = () => {
    setErroExportacao("");
    if (periodoInvalido) {
      setErroExportacao("A data inicial não pode ser maior que a final.");
      return;
    }
    setLogPage(1);
    setLogFiltros({ de: logDe, ate: logAte });
  };

  const handleExportarLog = async () => {
    setErroExportacao("");
    if (periodoInvalido) {
      setErroExportacao("A data inicial não pode ser maior que a final.");
      return;
    }
    setExportando(true);
    try {
      const nome = await exportarLogCaptura({ de: logDe, ate: logAte, idEmbarcador: embarcadorId });
      addToast({ place: "tr", type: "success", title: "Exportado", description: `Arquivo ${nome} gerado.` });
    } catch (err) {
      if (montadoRef.current) setErroExportacao(await mensagemErroBlob(err, "Não foi possível exportar o log."));
    } finally {
      if (montadoRef.current) setExportando(false);
    }
  };

  if (!hasEmpresaSelection) {
    return (
      <div className="content certificado-digital-page">
        <SpecificEmpresaRequiredNotice message="Selecione um embarcador específico para gerenciar o certificado digital." />
      </div>
    );
  }

  const ocupado = carregando || enviando || removendoId !== null;

  return (
    <div className="content certificado-digital-page docs-frete">
      <PageHero
        eyebrow="Conexões"
        title="Certificado digital"
        description="Envie o certificado A1 da sua empresa para a Everlog buscar direto na SEFAZ os CT-e em que você é o tomador. O arquivo e a senha ficam em cofre gerenciado; ninguém da Everlog tem acesso a eles."
        actions={<PageHeroRefreshButton onClick={() => carregar()} loading={carregando} />}
      />

      <ShellCard
        eyebrow="Cofre"
        title="Certificados ativos"
        description="Um certificado por raiz de CNPJ. Ele cobre todos os estabelecimentos do seu cadastro com a mesma raiz."
        action={
          ativos.length ? (
            <Badge color="info" pill>
              {ativos.length} ativo{ativos.length === 1 ? "" : "s"}
            </Badge>
          ) : null
        }
        style={{ marginTop: "1.25rem" }}
      >
        {erroLista ? <div style={{ ...erroStyle, marginTop: 0, marginBottom: "0.75rem" }}>{erroLista}</div> : null}
        {carregando && certificados.length === 0 ? (
          <div style={{ padding: "0.75rem 0" }}>
            <Spinner size="sm" color="info" />
          </div>
        ) : ativos.length === 0 ? (
          <div style={vazioStyle}>
            Nenhum certificado no cofre. Sem ele, a captura direta na SEFAZ não roda para este embarcador — os CT-e continuam
            chegando só pelo SFTP das transportadoras.
          </div>
        ) : (
          ativos.map((cert) => (
            <LinhaCertificado
              key={cert.id}
              cert={cert}
              confirmando={confirmandoId === cert.id}
              removendo={removendoId === cert.id}
              ocupado={ocupado}
              onPedirRemocao={() => {
                setErroLista("");
                setConfirmandoId(cert.id);
              }}
              onConfirmar={() => handleRemover(cert)}
              onCancelar={() => setConfirmandoId(null)}
            />
          ))
        )}
      </ShellCard>

      <ShellCard
        eyebrow="Novo certificado"
        title={ativos.length ? "Enviar ou substituir certificado" : "Enviar certificado"}
        description="Os dados do certificado são lidos antes de guardar: senha, validade, cadeia ICP-Brasil e se a raiz do CNPJ é de um estabelecimento do seu cadastro. Se algo não bater, nada é gravado."
      >
        <form onSubmit={handleEnviar} autoComplete="off">
          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="certificado-arquivo" style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600 }}>
                  Arquivo do certificado (A1)
                </Label>
                {/* PO1-3427: o <input type="file"> nativo some no tema escuro; fica oculto e
                    o botão abre o seletor (mesmo padrão de TabelasFrete/AnexosTabela). */}
                <input
                  id="certificado-arquivo"
                  ref={fileInputRef}
                  type="file"
                  accept={CERTIFICADO_ACCEPT}
                  style={{ display: "none" }}
                  disabled={enviando}
                  onChange={handleSelecionar}
                  data-testid="certificado-input"
                />
                <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                  <Button
                    outline
                    color="primary"
                    size="sm"
                    type="button"
                    disabled={enviando}
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    style={{ margin: 0 }}
                    data-testid="certificado-escolher"
                  >
                    <i className="tim-icons icon-cloud-upload-94" /> Escolher arquivo
                  </Button>
                  <span
                    style={{ color: arquivo ? AUTOPILOT_COLORS.text : AUTOPILOT_COLORS.muted, fontSize: "0.84rem", wordBreak: "break-all" }}
                    data-testid="certificado-arquivo-nome"
                  >
                    {arquivo && arquivo.name ? arquivo.name : "Nenhum arquivo selecionado"}
                  </span>
                </div>
                <small style={legendaStyle}>{CERTIFICADO_LEGENDA}</small>
                {erroArquivo ? <div style={erroStyle}>{erroArquivo}</div> : null}
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label for="certificado-senha" style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600 }}>
                  Senha do certificado
                </Label>
                <Input
                  id="certificado-senha"
                  type="password"
                  autoComplete="new-password"
                  value={senha}
                  disabled={enviando}
                  onChange={(e) => setSenha(e.target.value)}
                  placeholder="A senha definida quando o certificado foi emitido"
                  data-testid="certificado-senha"
                />
                <small style={legendaStyle}>
                  Usada uma única vez para abrir o arquivo e guardá-lo no cofre. Não fica no seu navegador nem nos registros.
                </small>
              </FormGroup>
            </Col>
          </Row>

          <SectionCard
            eyebrow={termo && termo.versao ? `Termo · versão ${termo.versao}` : "Termo"}
            title={termo ? termo.titulo : "Autorização de uso do certificado"}
            style={{ marginTop: "0.5rem" }}
          >
            {termo ? (
              <div style={termoBoxStyle} data-testid="certificado-termo">
                <p style={{ marginBottom: "0.6rem" }}>{termo.preambulo}</p>
                <ol style={{ paddingLeft: "1.2rem", marginBottom: "0.6rem" }}>
                  {(termo.itens || []).map((item, i) => (
                    <li key={i} style={{ marginBottom: "0.4rem" }}>
                      <span style={termoItemTituloStyle}>{item.titulo}.</span> {item.texto}
                    </li>
                  ))}
                </ol>
                <p style={{ marginBottom: 0 }}>{termo.fecho}</p>
              </div>
            ) : (
              <div style={vazioStyle}>
                {carregando ? <Spinner size="sm" color="info" /> : "Termo indisponível no momento. Atualize a página."}
              </div>
            )}
            <FormGroup check style={{ marginTop: "0.75rem" }}>
              <Label check style={{ color: AUTOPILOT_COLORS.title, fontSize: "0.88rem" }}>
                <Input
                  type="checkbox"
                  checked={aceite}
                  disabled={enviando || !termo}
                  onChange={(e) => setAceite(e.target.checked)}
                  data-testid="certificado-aceite"
                />{" "}
                Li e autorizo a Everlog a usar este certificado nos termos acima
                {termo && termo.versao ? ` (${termo.versao})` : ""}.
                <span className="form-check-sign" />
              </Label>
            </FormGroup>
          </SectionCard>

          {arquivo && raizesAtivas.length ? (
            <div style={avisoStyle}>
              Já existe certificado ativo para {raizesAtivas.length === 1 ? "a raiz" : "as raízes"}{" "}
              <span style={monoStyle}>{raizesAtivas.map(formatarRaiz).join(", ")}</span>. Se o novo arquivo for da mesma
              raiz, ele substitui o atual na hora; se for de outra, os dois ficam ativos.
            </div>
          ) : null}
          {erroEnvio ? <div style={erroStyle}>{erroEnvio}</div> : null}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "1rem", flexWrap: "wrap" }}>
            <Button
              color="secondary"
              outline
              size="sm"
              type="button"
              disabled={enviando || (!arquivo && !senha && !aceite)}
              onClick={limparFormulario}
            >
              Limpar
            </Button>
            <Button color="info" size="sm" type="submit" disabled={!podeEnviar} data-testid="certificado-enviar">
              {enviando ? (
                <>
                  <Spinner size="sm" style={{ marginRight: 6 }} /> Validando e guardando...
                </>
              ) : (
                <>
                  <i className="tim-icons icon-cloud-upload-94" style={{ marginRight: 6 }} /> Enviar para o cofre
                </>
              )}
            </Button>
          </div>
        </form>
      </ShellCard>

      <ShellCard
        eyebrow="Auditoria"
        title="Log da captura na SEFAZ"
        description="Cada consulta feita à SEFAZ com o seu certificado: quando, qual CNPJ, faixa de NSU, resposta e os CT-e trazidos (chave e resultado). Exporte em CSV para conferir sem depender do suporte."
      >
        <Row className="align-items-end">
          <Col md="3">
            <FormGroup>
              <Label for="log-de" style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600 }}>
                De
              </Label>
              <Input id="log-de" type="date" value={logDe} max={logAte || hojeIso} onChange={(e) => setLogDe(e.target.value)} disabled={exportando} />
            </FormGroup>
          </Col>
          <Col md="3">
            <FormGroup>
              <Label for="log-ate" style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600 }}>
                Até
              </Label>
              <Input id="log-ate" type="date" value={logAte} min={logDe} max={hojeIso} onChange={(e) => setLogAte(e.target.value)} disabled={exportando} />
            </FormGroup>
          </Col>
          <Col md="6" style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", flexWrap: "wrap", paddingBottom: "1rem" }}>
            <Button color="info" size="sm" outline onClick={handleConsultarLog} disabled={logCarregando} data-testid="certificado-consultar-log">
              {logCarregando ? (
                <>
                  <Spinner size="sm" style={{ marginRight: 6 }} /> Consultando...
                </>
              ) : (
                "Consultar"
              )}
            </Button>
            <Button color="success" size="sm" onClick={handleExportarLog} disabled={exportando || !ativos.length} data-testid="certificado-exportar-log">
              {exportando ? (
                <>
                  <Spinner size="sm" style={{ marginRight: 6 }} /> Exportando...
                </>
              ) : (
                "Exportar CSV"
              )}
            </Button>
          </Col>
        </Row>
        {!ativos.length ? <small style={legendaStyle}>Sem certificado ativo não há captura para auditar.</small> : null}
        {erroExportacao ? <div style={erroStyle}>{erroExportacao}</div> : null}

        {/* PO1-3426: log paginado no servidor; a linha abre o detalhe com os documentos. */}
        <TableSurface style={{ marginTop: "0.75rem" }}>
          <EstadoTabela
            loading={logCarregando && !logItens.length}
            erro={erroLog}
            vazio={!logCarregando && !logItens.length}
            textoCarregando="Consultando o log da captura..."
            textoVazio="Nenhuma consulta à SEFAZ no período."
          />
          {!erroLog && logItens.length ? (
            <>
              <Table className="docs-frete__tabela hub-thead-accent" size="sm" responsive hover data-testid="certificado-log-tabela">
                <thead className="text-primary">
                  <tr>
                    <th>Data e hora</th>
                    <th>CNPJ</th>
                    <th>NSU</th>
                    <th>Resposta</th>
                    <th className="is-number">Docs</th>
                    <th className="is-number">Gravados</th>
                    <th className="is-number">Já existentes</th>
                    <th className="is-number">Erros</th>
                    <th className="is-number">Eventos</th>
                  </tr>
                </thead>
                <tbody>
                  {logItens.map((item) => (
                    <LinhaLog key={item._id} item={item} onAbrir={() => setLogAberto(item)} />
                  ))}
                </tbody>
              </Table>
              <TablePagination
                page={logPage}
                pageSize={logPageSize}
                total={logTotal}
                pageSizes={PAGE_SIZES}
                itemLabel="consulta(s)"
                disabled={logCarregando}
                onPageChange={setLogPage}
                onPageSizeChange={(novo) => {
                  setLogPageSize(novo);
                  setLogPage(1);
                }}
              />
            </>
          ) : null}
        </TableSurface>
      </ShellCard>

      {logAberto ? (
        <DetalheLogModal
          item={logAberto}
          detalhe={logDetalhe}
          carregando={logDetalheCarregando}
          erro={erroLogDetalhe}
          onClose={() => setLogAberto(null)}
        />
      ) : null}

      {historico.length ? (
        <ShellCard
          eyebrow="Histórico"
          title="Certificados removidos ou substituídos"
          description="Ficam só os metadados. O arquivo e a senha foram apagados do cofre no ato da remoção."
          action={
            <Button color="link" size="sm" onClick={() => setHistoricoAberto((v) => !v)} style={{ padding: 0 }}>
              {historicoAberto ? "Ocultar" : `Mostrar ${historico.length}`}
            </Button>
          }
        >
          <Collapse isOpen={historicoAberto}>
            {historico.map((cert) => {
              const situacao = situacaoCertificado(cert);
              return (
                <div key={cert.id} style={linhaStyle}>
                  <i className="tim-icons icon-lock-circle" style={{ color: AUTOPILOT_COLORS.textFaint, fontSize: "1.1rem", marginTop: 3 }} />
                  <div style={{ flex: 1, minWidth: 240 }}>
                    <div style={{ ...tituloLinhaStyle, color: AUTOPILOT_COLORS.textSoft }}>
                      {cert.razaoSocial || "Certificado A1"}{" "}
                      <Badge color={situacao.cor} pill style={{ marginLeft: 6, verticalAlign: "middle" }}>
                        {situacao.rotulo}
                      </Badge>
                    </div>
                    <div style={metaStyle}>
                      CNPJ <span style={monoStyle}>{formatarCnpj(cert.cnpjCertificado)}</span> · válido até{" "}
                      <span style={monoStyle}>{formatDate(cert.validadeFim)}</span>
                      <br />
                      Enviado em {formatDateTime(cert.dataUpload)} · {situacao.rotulo.toLowerCase()} em {formatDateTime(cert.dataRemocao)}
                    </div>
                  </div>
                </div>
              );
            })}
          </Collapse>
        </ShellCard>
      ) : null}
    </div>
  );
}
